import { Component, Vue } from 'vue-property-decorator';
import { Mutation, State } from 'vuex-class';
import { IGame } from '~/store/games/types';

/**
 * Use me when you want to use the GameActionButton component
 * Make sure to call my gameActionButtonHostCreated() function
 * when your created() function is called. And my
 * gameActionButtonHostDestroyed() function when your
 * destroyed() function is called. Use my gamesInstallStatuses
 * property to get the install status of the games.
 * (most or all of this logic could probably be moved into
 * the GameActionButton component, but that looked like a
 * bigger refactor)
 */

@Component
export default class GameActionButtonHost extends Vue {
  @Mutation toggleErrorSnackbar!: (payload?: boolean) => void;
  @Mutation updateSnackbarErrorText!: (args: any) => void;

  @State(games => games.games, { namespace: 'games' })
  games!: IGame[];

  GameLauncherService = this.$initGameLauncherService();
  launcherOpenListenerHandle: any = null;
  launcherCloseListenerHandle: any = null;
  launcherMessageListenerHandle: any = null;
  launcherMessageProcessedEventName = 'launcherMessageProcessed';
  renderIndex = 0;
  gamesInstallStatuses: {
    [key: string]: { isInstalled: boolean; version: string };
  } = {};

  gameActionButtonHostCreated() {
    if (this.GameLauncherService.socket.readyState !== WebSocket.OPEN) {
      this.launcherOpenListenerHandle = () => {
        this.checkForGamesInstalled();
        this.GameLauncherService.removeEventListener(
          'open',
          this.launcherOpenListenerHandle,
        );
      };

      this.GameLauncherService.addEventListener(
        'open',
        this.launcherOpenListenerHandle,
      );
    } else {
      this.checkForGamesInstalled();
    }
  }

  gameActionButtonHostDestroyed(args: { disableAutoReconnect?: boolean } = {}) {
    if (args.disableAutoReconnect) {
      this.GameLauncherService.autoReconnect = false;
    }

    if (this.launcherOpenListenerHandle) {
      this.GameLauncherService.removeEventListener(
        'open',
        this.launcherOpenListenerHandle,
      );
    }

    if (this.launcherCloseListenerHandle) {
      this.GameLauncherService.removeEventListener(
        'close',
        this.launcherCloseListenerHandle,
      );
    }

    if (this.launcherMessageListenerHandle) {
      this.GameLauncherService.removeEventListener(
        'message',
        this.launcherMessageListenerHandle,
      );
    }
  }

  async checkForGamesInstalled() {
    try {
      this.launcherMessageListenerHandle = (event: any) => {
        if (event && event.message) {
          if (event.message.error) {
            const errMessage = this.GameLauncherService.getDisplayErrorMessage(
              event.message.error,
            );
            if (errMessage) {
              this.updateSnackbarErrorText(errMessage);
              this.toggleErrorSnackbar(true);
            }
          } else if (event.message.command === 'version') {
            this.renderIndex++;
          } else if (event.message.type === 'downloadProgress') {
            this.renderIndex++;
          } else if (event.message.type === 'installComplete') {
            this.renderIndex++;

            this.gamesInstallStatuses[event.message.gameName] = {
              isInstalled: event.message.isInstalled,
              version: event.message.version,
            };
          } else if (event.message.type === 'updateComplete') {
            this.renderIndex++;

            this.gamesInstallStatuses[event.message.gameName] = {
              isInstalled: event.message.isInstalled,
              version: event.message.version,
            };
          } else if (event.message.type === 'startingInstall') {
            this.renderIndex++;
          } else if (event.message.command === 'areGamesInstalled') {
            this.gamesInstallStatuses = event.message;

            this.renderIndex++;

            this.launcherCloseListenerHandle = () => {
              this.renderIndex++;

              this.launcherOpenListenerHandle = () => {
                this.renderIndex++;
              };

              this.GameLauncherService.addEventListener(
                'open',
                this.launcherOpenListenerHandle,
              );
            };
            this.GameLauncherService.addEventListener(
              'close',
              this.launcherCloseListenerHandle,
            );
          }
        }

        this.$emit(this.launcherMessageProcessedEventName, event);
      };
      this.GameLauncherService.addEventListener(
        'message',
        this.launcherMessageListenerHandle,
      );

      // UA tracking
      this.$ua.trackLauncherInstalledGamesCheckBeginEvent();

      await this.GameLauncherService.send({
        command: 'areGamesInstalled',
        params: {
          games: this.games.map(game => game.codeName),
        },
      });
    } catch (err) {
      // UA tracking
      this.$ua.trackLauncherInstalledGamesCheckErrorEvent(err);
      console.warn(err);
    }
  }
}
